/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import {
  FIVE_DIGIT_WIDTH,
  FOUR_DIGIT_WIDTH,
  SIX_DIGIT_WIDTH,
  TEXT_WIDTH,
  THREE_DIGIT_WIDTH,
  TWO_DIGIT_WIDTH,
  defaultCellStyle,
  numberAsStringComparator,
  pointsGapToLeaderValueFormatter,
} from 'components/common/Tables/CommonGridFormatters';

export enum DriverPointsTableColId {
  Position = 'position',
  Vehicle = 'vehicleNumber',
  Driver = 'driverName',
  Laps = 'laps',
  GapToLeader = 'gapToLeader',
  PreRaceRank = 'preRaceRank',
  PreRaceWins = 'preRaceWins',
  PreRacePoints = 'preRacePoints',
  PreRacePlayoffPoints = 'preRacePlayoffPoints',
  PointsGained = 'pointsGained',
  CurrentWins = 'currentWins',
  CurrentPoints = 'currentPoints',
  CurrentRank = 'currentRank',
  GapTo = 'pointsGap',
  ClinchPoints = 'pointsToClinch',
}

export enum ErdpDriverPointsTableColId {
  VehicleNumber = 'vehicleNumber',
  DeltaLeader = 'deltaLeader',
  DeltaNext = 'deltaNext',
  FirstName = 'firstName',
  DriverId = 'driverId',
  IsInChase = 'isInChase',
  IsPointsEligible = 'isPointsEligible',
  IsRookie = 'isRookie',
  LastName = 'lastName',
  MembershipId = 'membershipId',
  Points = 'points',
  PointsPosition = 'pointsPosition',
  PointsEarnedThisRace = 'pointsEarnedThisRace',
  StageOnePoints = 'stageOnePoints',
  StageOneWinner = 'stageOneWinner',
  StageTwoPoints = 'stageTwoPoints',
  StageTwoWinner = 'stageTwoWinner',
  StageThreePoints = 'stageThreePoints',
  StageThreeWinner = 'stageThreeWinner',
  Wins = 'wins',
  TopFive = 'topFive',
  TopTen = 'topTen',
  Poles = 'poles',
  Driver = 'driver',
  AccruedPoints = 'accruedPoints',
  PointsAtStart = 'pointsAtStart',
}

const booleanToYesNo = (params: ICellRendererParams) => (params.value ? 'Yes' : 'No');

export const driverPointsTableColDefs: ColDef[] = [
  {
    colId: DriverPointsTableColId.Position,
    cellClass: 'stringType',
    cellStyle: defaultCellStyle,
    headerName: 'Pos',
    headerTooltip: 'Position',
    type: 'numericColumn',
    width: 64,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.Vehicle,
    headerName: 'Vehicle',
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
    width: 65,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.Driver,
    headerName: 'Driver',
    type: 'rightAligned',
    width: 140,
    minWidth: TEXT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.Laps,
    headerName: 'Laps',
    type: 'numericColumn',
    width: 50,
    minWidth: THREE_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.GapToLeader,
    headerName: 'Gap to Leader',
    headerTooltip: 'Time between this car and the lead car',
    type: 'numericColumn',
    valueFormatter: pointsGapToLeaderValueFormatter,
    width: 107,
    minWidth: SIX_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.PreRaceRank,
    headerName: 'Pre-race Rank',
    headerTooltip: 'Season Rank, sorted by wins then points at start of the race',
    type: 'numericColumn',
    width: 106,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.PreRaceWins,
    headerName: 'Pre-race Wins',
    headerTooltip: 'Season Wins at start of the race',
    type: 'numericColumn',
    width: 106,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.PreRacePoints,
    headerName: 'Pre-race Points',
    headerTooltip: 'Season Points at start of the race',
    type: 'numericColumn',
    width: 115,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.PreRacePlayoffPoints,
    headerName: 'Pre-race Playoff Points',
    headerTooltip: 'Playoff points at start of the race',
    type: 'numericColumn',
    width: 163,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.PointsGained,
    headerName: 'Points Gained',
    headerTooltip: 'Points gained in the race so far',
    type: 'numericColumn',
    width: 106,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.CurrentWins,
    headerName: 'Current Wins',
    headerTooltip: 'Season Wins for end of race, depending on if car places 1st',
    type: 'numericColumn',
    width: 99,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.CurrentPoints,
    headerName: 'Current Points',
    headerTooltip: 'Points at start of the race plus any points gained to this point in the race.',
    type: 'numericColumn',
    width: 110,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.CurrentRank,
    headerName: 'Current Rank',
    headerTooltip: 'Season Rank, sorted by current wins then current points for end of the race',
    type: 'numericColumn',
    width: 100,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.GapTo,
    headerName: 'Gap To',
    headerTooltip:
      'Number of points ahead or behind a desired car or rank specified in Playoff Config using Current points',
    type: 'numericColumn',
    width: 64,
    minWidth: FIVE_DIGIT_WIDTH,
  },
  {
    colId: DriverPointsTableColId.ClinchPoints,
    headerName: 'Clinch Points',
    headerTooltip: 'Points needed to meet the cutoff rank specified in Playoff Config',
    type: 'numericColumn',
    width: 101,
    minWidth: FOUR_DIGIT_WIDTH,
  },
];

export const erdpDriverPointsTableColDefs: ColDef[] = [
  {
    colId: ErdpDriverPointsTableColId.Driver,
    headerName: 'Driver',
    type: 'rightAligned',
    width: 105,
    minWidth: TEXT_WIDTH,
    valueGetter: (params) =>
      `${params.data[ErdpDriverPointsTableColId.FirstName]} ${params.data[ErdpDriverPointsTableColId.LastName]}`,
  },
  {
    colId: ErdpDriverPointsTableColId.FirstName,
    headerName: 'First Name',
    type: 'rightAligned',
    width: 80,
    minWidth: TEXT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.LastName,
    headerName: 'Last Name',
    type: 'rightAligned',
    width: 80,
    minWidth: TEXT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.VehicleNumber,
    headerName: 'Vehicle',
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
    width: 65,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.PointsPosition,
    headerName: 'Pos',
    headerTooltip: 'Points position',
    type: 'numericColumn',
    width: 65,
    minWidth: TWO_DIGIT_WIDTH,
    sort: 'asc',
  },
  {
    colId: ErdpDriverPointsTableColId.Points,
    headerName: 'CurPts',
    headerTooltip: 'Current Points',
    type: 'numericColumn',
    width: 65,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.PointsEarnedThisRace,
    headerName: 'RacePts',
    headerTooltip: 'Points gained in the race so far',
    type: 'numericColumn',
    width: 65,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.StageOnePoints,
    headerName: 'Stg1Pts',
    headerTooltip: 'Stage 1 Points',
    type: 'numericColumn',
    width: 70,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.StageOneWinner,
    headerName: 'Stg1Win',
    type: 'rightAligned',
    cellRenderer: booleanToYesNo,
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.StageTwoPoints,
    headerName: 'Stg2Pts',
    headerTooltip: 'Stage 2 Points',
    type: 'numericColumn',
    width: 70,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.StageTwoWinner,
    headerName: 'Stg2Win',
    type: 'rightAligned',
    cellRenderer: booleanToYesNo,
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.StageThreePoints,
    headerName: 'Stg3Pts',
    headerTooltip: 'Stage 3 Points',
    type: 'numericColumn',
    width: 70,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.StageThreeWinner,
    headerName: 'Stg3Win',
    type: 'rightAligned',
    cellRenderer: booleanToYesNo,
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.DeltaLeader,
    headerName: 'GapToLead',
    headerTooltip: 'Points delta between this driver and the points leader',
    type: 'numericColumn',
    width: 95,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.DeltaNext,
    headerName: 'GapToNext',
    headerTooltip: 'Points delta between this driver and the next driver',
    type: 'numericColumn',
    width: 95,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.Wins,
    headerName: 'TotWins',
    headerTooltip: 'Season Wins for end of race, depending on if car places 1st',
    type: 'numericColumn',
    width: 70,
    minWidth: TWO_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.TopFive,
    headerName: 'Top5s',
    headerTooltip: 'Number of top 5 finishes',
    type: 'numericColumn',
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.TopTen,
    headerName: 'Top10s',
    headerTooltip: 'Number of top 10 finishes',
    type: 'numericColumn',
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.Poles,
    headerName: 'Poles',
    headerTooltip: 'Number of pole positions',
    type: 'numericColumn',
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.IsInChase,
    headerName: 'InChase',
    type: 'rightAligned',
    cellRenderer: booleanToYesNo,
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.IsPointsEligible,
    headerName: 'PtsEligible',
    type: 'rightAligned',
    cellRenderer: booleanToYesNo,
    width: 90,
  },
  {
    colId: ErdpDriverPointsTableColId.IsRookie,
    headerName: 'Rookie',
    type: 'rightAligned',
    cellRenderer: booleanToYesNo,
    width: 65,
  },
  {
    colId: ErdpDriverPointsTableColId.AccruedPoints,
    headerName: 'Accrued Points',
    headerTooltip: 'Points accrued during Stage 1 and Stage 2',
    type: 'numericColumn',
    width: 65,
    minWidth: FOUR_DIGIT_WIDTH,
  },
  {
    colId: ErdpDriverPointsTableColId.PointsAtStart,
    headerName: 'Points At Start',
    headerTooltip: 'Points the Driver had at the start of the race',
    type: 'numericColumn',
    hide: true,
    width: 65,
    minWidth: FOUR_DIGIT_WIDTH,
  },
];
